import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { isSpaPath } from './spaPath'

function isPlainLeftClick(event: MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
}

/**
 * Turns clicks on links to signed-in screens into client-side navigations, wherever the href came
 * from. It listens on the document in the bubble phase, so React's own onClick handlers run first
 * and a handler that calls preventDefault keeps the link to itself.
 */
export function useInternalLinkNavigation() {
  const navigate = useNavigate()

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || !isPlainLeftClick(event) || !(event.target instanceof Element)) {
        return
      }
      const anchor = event.target.closest('a[href]')
      if (!(anchor instanceof HTMLAnchorElement) || anchor.target || anchor.hasAttribute('download')) {
        return
      }
      const url = new URL(anchor.href)
      const isSamePage = url.pathname === window.location.pathname && url.search === window.location.search
      const isHashJump = isSamePage && (url.hash !== '' || anchor.getAttribute('href') === '#')
      if (isHashJump || !isSpaPath(url)) {
        return
      }
      event.preventDefault()
      anchor.blur()
      navigate(url.pathname + url.search + url.hash, { replace: isSamePage })
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [navigate])
}
